import { theme as antdTheme } from 'antd';
import { KANAGAWA_DRAGON, KANAGAWA_LOTUS } from './kanagawa';
export const AID_SCROLLBAR_CSS_VARIABLES = {
    track: '--aid-scrollbar-track',
    thumb: '--aid-scrollbar-thumb',
    thumbHover: '--aid-scrollbar-thumb-hover',
};
const spacingTokens = {
    padding: 16,
    paddingLG: 24,
    paddingXL: 32,
    margin: 16,
    marginLG: 24,
    marginXL: 32,
};
export function buildKanagawaScrollbarTokens(mode) {
    const palette = mode === 'dark' ? KANAGAWA_DRAGON : KANAGAWA_LOTUS;
    return {
        track: palette.mantle,
        thumb: palette.surface1,
        thumbHover: palette.surface2,
    };
}
export function buildKanagawaTheme(mode) {
    const palette = mode === 'dark' ? KANAGAWA_DRAGON : KANAGAWA_LOTUS;
    const isDark = mode === 'dark';
    const modalSeparator = `1px solid ${palette.surface1}`;
    // Elevation shadows tinted toward the palette so cards read as physical
    // machined surfaces rather than flat tiles.
    const cardShadow = isDark
        ? '0 1px 0 rgba(0, 0, 0, 0.32) inset, 0 1px 2px rgba(0, 0, 0, 0.45)'
        : '0 1px 0 rgba(255, 255, 255, 0.4) inset, 0 1px 2px rgba(84, 84, 109, 0.06), 0 4px 12px rgba(84, 84, 109, 0.05)';
    const modalTokens = {
        contentBg: palette.surface0,
        headerBg: palette.surface0,
        footerBg: palette.surface0,
        titleColor: palette.text,
        contentPadding: 0,
        headerPadding: spacingTokens.paddingLG,
        headerBorderBottom: modalSeparator,
        headerMarginBottom: 0,
        bodyPadding: spacingTokens.paddingLG,
        footerPadding: spacingTokens.paddingLG,
        footerBorderTop: modalSeparator,
        footerMarginTop: 0,
    };
    return {
        algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        token: {
            colorPrimary: palette.blue,
            colorInfo: palette.blue,
            colorSuccess: palette.green,
            colorError: palette.red,
            colorWarning: palette.peach,
            colorBgBase: palette.base,
            colorText: palette.text,
            colorBgContainer: palette.mantle,
            colorBgElevated: palette.surface0,
            colorBorderSecondary: palette.surface1,
            borderRadius: 8,
            borderRadiusLG: 10,
            colorLink: palette.mauve,
            fontSize: 14,
            fontSizeLG: 16,
            fontSizeXL: 18,
            fontSizeHeading1: 32,
            fontSizeHeading2: 26,
            fontSizeHeading3: 22,
            fontSizeHeading4: 18,
            fontSizeHeading5: 15,
            lineHeight: 1.55,
            controlHeight: 36,
            controlHeightLG: 40,
            padding: spacingTokens.padding,
            paddingLG: spacingTokens.paddingLG,
            paddingXL: spacingTokens.paddingXL,
            margin: spacingTokens.margin,
            marginLG: spacingTokens.marginLG,
            marginXL: spacingTokens.marginXL,
            boxShadow: cardShadow,
            boxShadowSecondary: isDark
                ? '0 6px 16px rgba(0, 0, 0, 0.4)'
                : '0 6px 20px rgba(84, 84, 109, 0.08)',
        },
        components: {
            Layout: {
                headerBg: isDark ? KANAGAWA_DRAGON.surface0 : KANAGAWA_LOTUS.mantle,
                headerHeight: 60,
                headerPadding: '0 28px',
                bodyBg: palette.base,
                footerBg: 'transparent',
                footerPadding: '24px 28px',
            },
            Modal: modalTokens,
            Card: {
                headerBg: 'transparent',
                headerHeight: 52,
                headerHeightSM: 44,
                paddingLG: 24,
                boxShadow: cardShadow,
                boxShadowTertiary: cardShadow,
            },
            Menu: {
                itemBg: 'transparent',
                itemSelectedBg: isDark ? 'rgba(126, 156, 216, 0.14)' : 'rgba(77, 105, 155, 0.10)',
                itemSelectedColor: palette.blue,
                itemHoverBg: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(84, 84, 109, 0.05)',
                itemBorderRadius: 6,
                itemHeight: 36,
                iconSize: 16,
            },
            Button: {
                controlHeight: 36,
                controlHeightLG: 40,
                borderRadius: 8,
                borderRadiusLG: 10,
                fontWeight: 500,
                primaryShadow: 'none',
                defaultShadow: 'none',
            },
            Input: {
                controlHeight: 36,
                borderRadius: 8,
                activeShadow: `0 0 0 2px ${isDark ? 'rgba(126, 156, 216, 0.20)' : 'rgba(77, 105, 155, 0.16)'}`,
            },
            Select: {
                controlHeight: 36,
                borderRadius: 8,
            },
            DatePicker: {
                controlHeight: 36,
                borderRadius: 8,
            },
            Form: {
                labelColor: isDark ? palette.text : palette.subtext1,
            },
            Table: {
                headerBg: isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(84, 84, 109, 0.04)',
                headerColor: palette.subtext1,
                rowHoverBg: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(84, 84, 109, 0.03)',
                borderColor: palette.surface1,
                cellPaddingBlock: 12,
                cellPaddingInline: 16,
            },
            Tag: {
                borderRadiusSM: 6,
            },
            Statistic: {
                titleFontSize: 12,
                contentFontSize: 26,
            },
            Tabs: {
                titleFontSize: 14,
                horizontalItemPadding: '10px 0',
                horizontalMargin: '0 0 24px 0',
            },
            Typography: {
                titleMarginBottom: 12,
                titleMarginTop: 0,
            },
        },
    };
}
//# sourceMappingURL=theme.js.map